<script lang="ts">
  import Login from "$lib/components/Login.svelte";
  import Register from "$lib/components/Register.svelte";
  import {translate} from "$lib/i18n";
  import { fade, slide } from 'svelte/transition';
  import { onMount } from 'svelte';

  interface Props {
    isLogin?: boolean;
  }

  let { isLogin = $bindable(true) }: Props = $props();

  // Set initial minimum heights to prevent empty display
  let loginHeight = 400; // Default minimum height
  let registerHeight = 600; // Default minimum height
  let formContainer;
  let mounted = false;

  onMount(() => {
    // Mark as mounted to enable transitions
    mounted = true;

    // Force a reflow to ensure heights are calculated
    setTimeout(() => {
      if (formContainer) {
        formContainer.style.display = 'none';
        void formContainer.offsetHeight; // Force reflow
        formContainer.style.display = '';
      }
    }, 0);
  });
</script>

<div class="flex justify-center items-center flex-col p-2 sm:p-4 text-white max-w-md mx-auto">
  <!-- Enhanced Tab Navigation with new background color and responsive padding -->
  <div class="flex justify-center items-center w-full mb-4 sm:mb-6 bg-[rgb(28,37,53)] rounded-full p-1">
    <button
            class="w-1/2 py-2 sm:py-2.5 px-2 sm:px-4 rounded-full text-xs sm:text-sm font-medium transition-all duration-200 {isLogin ? 'bg-[#FF3E00] text-white shadow-lg' : 'text-gray-400 hover:text-white'}"
            on:click={() => isLogin = true}
    >
      {$translate('Login')}
    </button>
    <button
            class="w-1/2 py-2 sm:py-2.5 px-2 sm:px-4 rounded-full text-xs sm:text-sm font-medium transition-all duration-200 {!isLogin ? 'bg-[#FF3E00] text-white shadow-lg' : 'text-gray-400 hover:text-white'}"
            on:click={() => isLogin = false}
    >
      {$translate('Register')}
    </button>
  </div>

  <!-- Form Container with Improved Transition -->
  <div
          bind:this={formContainer}
          class="w-full overflow-hidden transition-all duration-300"
          style="min-height: {isLogin ? loginHeight : registerHeight}px"
  >
    {#if isLogin}
      <div
              class="form-container"
              in:fade={{ duration: 300, delay: mounted ? 0 : 0 }}
              out:fade={{ duration: 200 }}
              on:introend={() => {
          // Update height after animation completes
          const el = document.querySelector('.login-form');
          if (el) loginHeight = Math.max(el.clientHeight, loginHeight);
        }}
      >
        <div class="login-form">
          <Login />
        </div>
      </div>
    {:else}
      <div
              class="form-container"
              in:slide={{ duration: 300, axis: 'y', delay: mounted ? 0 : 0 }}
              out:fade={{ duration: 200 }}
              on:introend={() => {
          // Update height after animation completes
          const el = document.querySelector('.register-form');
          if (el) registerHeight = Math.max(el.clientHeight, registerHeight);
        }}
      >
        <div class="register-form">
          <Register />
        </div>
      </div>
    {/if}
  </div>
</div>

<style>
  /* Add subtle gradient to the active tab */
  button.bg-\[\#FF3E00\] {
    background-image: linear-gradient(to right, #FF3E00, #FF5722);
  }

  /* Ensure forms are visible */
  .form-container {
    width: 100%;
  }

  /* Remove blue outline from input fields */
  :global(input),
  :global(select),
  :global(textarea) {
    outline: none !important;
    box-shadow: none !important;
  }

  :global(input:focus),
  :global(select:focus),
  :global(textarea:focus) {
    outline: none !important;
    box-shadow: none !important;
    border-color: rgba(255, 62, 0, 0.5) !important; /* Optional: add a subtle orange border instead */
  }

  /* Add responsive styles */
  @media (max-width: 480px) {
    button {
      font-size: 0.875rem;
      padding-left: 0.5rem;
      padding-right: 0.5rem;
    }
  }
</style>